import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Form,
  Select,
  Input,
  message as antMessage,
  Button,
  Switch
} from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import { genders } from '../../utils/constants/enums';
import useErrorMessage from '../../utils/ErrorMessage';
import useXmlPattern from '../../utils/xmlPattern';
import useContractsContext from './ContractsContext';
import useAuthContext from '../../contexts/AuthContext';
import useGenerateFormItem from '../../utils/GenerateFormItem';
import {
  isMetropolianAndMonacoZipcode,
  isMetropolitanZipcode,
  isValidZipcode
} from '../../utils/zipcode';
import { dromComAllowedTypes } from '../quotes/lastStepFields';

const { Option } = Select;

export const UpdateDetailsDrawer = ({ customer, id, type }) => {
  const { t } = useTranslation();
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const generateFormItem = useGenerateFormItem();
  const [form] = Form.useForm();
  const {
    updateDetailsVisible,
    setUpdateDetailsVisible,
    forceRefresh,
    setForceRefresh
  } = useContractsContext();
  const [postalCode, setPostalCode] = useState();
  const [cities, setCities] = useState();
  const xmlPattern = useXmlPattern();
  const [isLoading, setIsLoading] = useState(false);
  const [addressSuggestion, setAddressSuggestion] = useState([]);
  const [inseeLoading, setInseeLoading] = useState(false);
  const [siretStatus, setSiretStatus] = useState(true);
  const [selectContract, setSelectContract] = useState(false);
  const [modificationEmail, setModificationEmail] = useState(false);
  const [rnaStatus, setRnaStatus] = useState(true);
  const [allowUpdateDataOnEditMode, setAllowUpdateDataOnEditMode] =
    useState(false);

  const isCustomer = user?.role === 'guests:GUEST';

  const siretRequired = ['PREVOYANCE', 'MRI'];

  const postalCodeWatcher = Form.useWatch(['address', 'postal_code'], form);

  const loweredType = useMemo(() => type?.toLowerCase?.() || '', [type]);

  useEffect(() => {
    if (updateDetailsVisible && customer) {
      form.setFieldsValue(customer);
      setAllowUpdateDataOnEditMode(false);
    }
  }, [updateDetailsVisible, customer]);

  useEffect(() => {
    // In edit mode, we want to handlePostalCodeChange only when the user udpdate it (and not the first time the form is updated with saved data)
    if (allowUpdateDataOnEditMode) {
      handlePostalCodeChange(postalCodeWatcher);
    } else {
      setAllowUpdateDataOnEditMode(true);
    }
  }, [postalCodeWatcher]);

  const getAddressAutocomplete = async (address_autocomplete) => {
    if (address_autocomplete) {
      setIsLoading(true);
      try {
        const { data } = await dispatchAPI('GET', {
          url: `/places/${address_autocomplete}`
        });
        setAddressSuggestion(data?.predictions);
      } catch (e) {
        if (e.response) message(e.response.status);
      }
      setIsLoading(false);
    }
  };

  const getAddressPlaceId = async (placeId) => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/places/placeid/${placeId}`
      });

      form.setFieldsValue({
        address: {
          number: data.result.address_components[0].long_name,
          street: data.result.address_components[1].long_name,
          postal_code: data.result.address_components[6].long_name,
          city: data.result.address_components[2].long_name,
          address_autocomplete: ''
        }
      });
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const getCity = async (zipCode) => {
    try {
      if (!isValidZipcode(zipCode) || !isAllowedZipcode(zipCode)) {
        return [];
      }

      const { data } = await dispatchAPI('GET', {
        url: `/insee/cities/zipcode/${zipCode}`
      });

      const formattedCities = data.map((city) => ({
        ...city,
        label: `${city.name} (${city.zipcode})`
      }));

      return formattedCities;
    } catch (e) {
      if (e.response) message(e.response.status);
      return [];
    }
  };

  const handlePostalCodeChange = async (value) => {
    const cityList = await getCity(value);
    setCities(cityList);

    const address = form.getFieldValue('address') || {};

    if (!cityList?.length > 0) {
      if (address?.city) {
        form.setFieldsValue({
          address: {
            ...address,
            city: null
          }
        });
      }
      return;
    }

    const matchedCity = cityList.find(
      (city) => city.name?.toLowerCase() === address?.city?.toLowerCase()
    );

    const newSelectedCity = matchedCity || cityList[0];

    const newFormValues = {
      address: {
        ...address,
        city: newSelectedCity.name
      }
    };

    form.setFieldValue(['address', 'city'], newSelectedCity.name);
  };

  const checkEligibilyCodePostal = (codePostal) => {
    const postalCodesOutsideMetropolitanFrance = ['20', '97', '98'];
    let postalCodeMetropolitanFrance = true;
    postalCodesOutsideMetropolitanFrance.forEach((postalReference) => {
      if (codePostal.startsWith(postalReference))
        postalCodeMetropolitanFrance = false;
    });
    return postalCodeMetropolitanFrance;
  };

  const getSiretInfo = async (code) => {
    setInseeLoading(true);
    try {
      setSiretStatus(true);
      const {
        data: {
          etablissement: {
            siret,
            dateCreationEtablissement,
            adresseEtablissement: {
              numeroVoieEtablissement,
              typeVoieEtablissement,
              libelleVoieEtablissement,
              codePostalEtablissement,
              complementAdresseEtablissement,
              libelleCommuneEtablissement
            },
            uniteLegale: {
              denominationUniteLegale,
              nomUniteLegale,
              activitePrincipaleUniteLegale,
              etatAdministratifUniteLegale
            }
          }
        }
      } = await dispatchAPI('GET', {
        url: `/insee/${code}`
      });

      let tmpStreet = libelleVoieEtablissement;
      if (typeVoieEtablissement) {
        tmpStreet = `${typeVoieEtablissement} ${libelleVoieEtablissement}`;
      }

      const postalCodeMetropolitanFrance = checkEligibilyCodePostal(
        codePostalEtablissement
      );

      if (!postalCodeMetropolitanFrance) {
        setSiretStatus(false);
        message('company_outside_mainland', [60]);
      } else {
        const formattedResult = {
          company_status: etatAdministratifUniteLegale,
          naf: activitePrincipaleUniteLegale,
          siret,
          company_creation_date: moment(dateCreationEtablissement),
          company_name: denominationUniteLegale || nomUniteLegale,
          address: {
            number: numeroVoieEtablissement,
            street: tmpStreet,
            postal_code: codePostalEtablissement,
            additionnal: complementAdresseEtablissement,
            city: libelleCommuneEtablissement
          }
        };

        form.setFieldsValue({
          ...formattedResult
        });
      }
    } catch (e) {
      if (e.response) message('unknown_siren_siret_999');
      setSiretStatus(false);
    }
    setInseeLoading(false);
  };

  const getRnaInfo = async (code) => {
    setInseeLoading(true);
    try {
      const {
        data: {
          association: {
            id_association,
            adresse_numero_voie,
            adresse_type_voie,
            adresse_libelle_voie,
            adresse_code_postal,
            adresse_libelle_commune,
            titre,
            titre_court,
            objet,
            dirigeant_civilite
          }
        }
      } = await dispatchAPI('GET', {
        url: `/rna/${code.toUpperCase()}`
      });
      setRnaStatus(true);
      let tmpStreet = adresse_libelle_voie;
      if (adresse_type_voie) {
        tmpStreet = `${adresse_type_voie} ${adresse_libelle_voie}`;
      }
      let tmpGender;
      if (dirigeant_civilite === 'PM' || dirigeant_civilite === 'TM') {
        tmpGender = 'MISTER';
      }
      if (dirigeant_civilite === 'PF' || dirigeant_civilite === 'TF') {
        tmpGender = 'MADAME';
      }
      let tmpRepresentative;
      if (dirigeant_civilite === 'TF' || dirigeant_civilite === 'TM') {
        tmpRepresentative = 'TREASURER';
      }
      if (dirigeant_civilite === 'PF' || dirigeant_civilite === 'TM') {
        tmpRepresentative = 'PRESIDENT';
      }

      const postalCodeMetropolitanFrance =
        checkEligibilyCodePostal(adresse_code_postal);

      if (!postalCodeMetropolitanFrance) {
        setRnaStatus(false);
        message('company_outside_mainland', [60]);
      } else {
        const formattedResult = {
          customer: {
            naf: objet,
            rna: id_association,
            company_name: titre || titre_court,
            address: {
              number: adresse_numero_voie,
              street: tmpStreet,
              postal_code: adresse_code_postal,
              city: adresse_libelle_commune
            }
          }
        };
        if (tmpGender) {
          formattedResult.customer = {
            ...formattedResult.customer,
            gender: tmpGender,
            representative: tmpRepresentative
          };
        }
        form.setFieldsValue({
          ...formattedResult
        });
      }
    } catch (e) {
      setRnaStatus(false);
      if (e.response) message('unknown_rna_999', [60]);
    }
    setInseeLoading(false);
  };

  const isAllowedZipcode = (value) => {
    if (dromComAllowed) {
      return true;
    }
    if (loweredType === 'rcphx') {
      return isMetropolianAndMonacoZipcode(value);
    }
    return isMetropolitanZipcode(value);
  };

  const dromComAllowed = useMemo(
    () => dromComAllowedTypes.includes(loweredType),
    [type]
  );

  const fields = [
    ...(type && type !== 'ASSOCIATION'
      ? [
          {
            name: ['siret'],
            input: (
              <Input
                min={0}
                minLength={9}
                maxLength={14}
                onChange={async (e) => {
                  if (
                    (e.target.value &&
                      e.target.value.length !== 14 &&
                      e.target.value.length !== 9) ||
                    !e.target.value.length
                  ) {
                    setSiretStatus(false);
                  }
                  if (
                    e.target.value &&
                    (e.target.value.length === 14 ||
                      e.target.value.length === 9)
                  ) {
                    if (e.target.value && e.target.value === '99999999999999') {
                      setSiretStatus(true);
                    } else await getSiretInfo(e.target.value);
                  }
                }}
              />
            ),
            rules: [
              { required: !(type && siretRequired.includes(type)) },
              {
                validator: () =>
                  siretStatus
                    ? Promise.resolve()
                    : Promise.reject(new Error('SIRET invalide'))
              }
            ],
            hasFeedback: true,
            ...(inseeLoading
              ? { validateStatus: 'validating' }
              : {
                  ...(siretStatus || siretStatus === false
                    ? {
                        validateStatus: !siretStatus ? 'error' : 'success'
                      }
                    : {})
                })
          }
        ]
      : [
          {
            name: ['rna'],
            input: (
              <Input
                min={0}
                minLength={9}
                maxLength={14}
                onChange={async (e) => {
                  if (
                    (e.target.value &&
                      e.target.value.length !== 14 &&
                      e.target.value.length !== 9) ||
                    !e.target.value.length
                  ) {
                    setRnaStatus(false);
                  }
                  if (
                    e.target.value &&
                    (e.target.value.length === 14 ||
                      e.target.value.length === 9)
                  ) {
                    if (e.target.value && e.target.value === '99999999999999') {
                      setRnaStatus(true);
                    }
                    // else await getRnaInfo(inseeToken, e.target.value);
                  }
                }}
              />
            ),
            rules: [{ required: true }],
            hasFeedback: true,
            ...(inseeLoading
              ? { validateStatus: 'validating' }
              : {
                  ...(rnaStatus || rnaStatus === false
                    ? {
                        validateStatus: !rnaStatus ? 'error' : 'success'
                      }
                    : {})
                })
          }
        ]),
    {
      name: ['company_name'],
      label: t(
        `${type && type === 'MRI' ? 'legal_entity_name' : 'company_name'}`
      ),
      rules: [
        { required: !(type && siretRequired.includes(type)) },
        ...xmlPattern
      ]
    },
    {
      name: ['gender'],
      rules: [{ required: true }],
      input: (
        <Select>
          {genders.map((g) => (
            <Option value={g} key={g}>
              {t(`insurers.form.${g}`)}
            </Option>
          ))}
        </Select>
      )
    },
    {
      name: ['last_name'],
      rules: [{ required: true }, ...xmlPattern],
      input: <Input disabled={isCustomer} />
    },
    {
      name: ['first_name'],
      rules: [{ required: true }, ...xmlPattern],
      input: <Input disabled={isCustomer} />
    },
    {
      name: ['phone_number'],
      rules: [{ required: true }, { len: 10 }]
    },
    {
      name: [isCustomer ? 'email_client' : 'email_modification'],
      tooltip: true,
      input: (
        <Switch
          checkedChildren={t('settings.clientMenu.yes')}
          unCheckedChildren={t('settings.clientMenu.no')}
          onChange={(value) => setModificationEmail(value)}
          disabled={isCustomer}
        />
      )
    },

    {
      name: ['email'],
      rules: [{ required: true }, { type: 'email' }],
      input: <Input disabled={!modificationEmail} />
    },
    {
      name: ['address_autocomplete'],
      input: (
        <Select
          filterOption={false}
          showSearch
          loading={isLoading}
          showArrow={false}
          notFoundContent={null}
          onSearch={getAddressAutocomplete}
          onSelect={async (selected) => {
            const placeId = selected.split('#')[0];
            await getAddressPlaceId(placeId);
          }}
          options={
            addressSuggestion &&
            addressSuggestion.map((predictions) => ({
              label: predictions.description,
              value: `${predictions.place_id}#${predictions.description}`
            }))
          }
          autoComplete="anyrandomstring"
        />
      )
    },
    {
      name: ['address', 'number'],
      rules: [{ required: true }]
    },
    {
      name: ['address', 'street'],
      rules: [{ required: true }]
    },
    {
      name: ['address', 'additional']
    },
    {
      name: ['address', 'postal_code'],
      rules: [
        { required: true },
        {
          validator: (_, value, callback) => {
            if (!isValidZipcode(value)) {
              callback('Code postal invalide');
            } else if (!isAllowedZipcode(value)) {
              callback('Code postal non autorisé');
            } else {
              callback();
            }
          }
        }
      ],
      input: (
        <Input style={{ width: '90px' }} autoComplete="off" minLength={5} />
      )
    },
    {
      name: ['address', 'city'],
      rules: [{ required: true }],
      input: (
        <Select
          showSearch
          style={{ width: '250px' }}
          options={cities}
          fieldNames={{ value: 'name' }}
        />
      )
    }
  ];

  const onClose = () => {
    setUpdateDetailsVisible(false);
    form.resetFields();
    setForceRefresh(!forceRefresh);
  };

  const handleSubmit = async (body) => {
    setIsLoading(true);
    try {
      const { status } = await dispatchAPI('PATCH', {
        url: `customers/${id}?action=${
          selectContract ? 'ALL_CONTRACT' : 'SINGLE_CONTRACT'
        }`,
        body
      });
      if (status === 200) {
        antMessage.success(
          t('contracts.form.contract_status_info.customer_success')
        );
      }
      onClose();
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
  };

  return (
    <Drawer
      size={430}
      title="Modifier les coordonnées"
      open={updateDetailsVisible}
      onClose={onClose}
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={customer}
        onFinish={handleSubmit}
      >
        {fields.map((field) => generateFormItem('customers', field))}

        {/* <Form.Item
          label={t(
            `customers.form.modification_contract${
              isCustomer ? '_customer' : ''
            }`
          )}
          valuePropName="checked"
        >
          <Switch
            checkedChildren={t('settings.clientMenu.yes')}
            unCheckedChildren={t('settings.clientMenu.no')}
            onChange={(value) => setSelectContract(value)}
          />
        </Form.Item> */}
        <Form.Item style={{ marginTop: 25 }}>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {t('buttons.validate')}
            <CheckOutlined />
          </Button>
          <Button danger type="link" onClick={onClose}>
            {t('buttons.cancel')}
            <CloseOutlined />
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

UpdateDetailsDrawer.propTypes = {
  type: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  customer: PropTypes.shape({
    _id: PropTypes.string
  }).isRequired
};
